/**
 * 분할 보기 토글의 사용자 설정 — 호스트·뷰어 **독립**으로 기억한다.
 *
 * 저장소는 localStorage 다(세션이 아니라 이 브라우저의 취향이므로).
 * 프라이빗 모드 등으로 저장이 막혀도 화면은 정상 동작해야 한다 — 실패는
 * 조용히 무시하고 메모리 상태로만 산다(use-feed-order.ts 와 같은 규약).
 */

import { useCallback, useState } from 'react'
import {
  defaultSplitMode,
  isSplitMode,
  NON_UNIFIED_DEFAULT_SPLIT_MODE,
  splitStorageKey,
  type SplitMode,
} from './split-view'

export function useSplitView(role: 'host' | 'viewer'): {
  mode: SplitMode
  setMode: (mode: SplitMode) => void
  toggle: () => void
} {
  const [mode, setModeState] = useState<SplitMode>(() => {
    // P2(codex, fix2) — `localStorage` 접근 자체가 getter 를 통해 던질 수
    // 있다(예: 적대적/방어적 Object.defineProperty). typeof 검사를 try 밖에
    // 두면 그 경우 훅이 그대로 죽는다 — try 안으로 옮겨 catch 가 받는다.
    try {
      if (typeof localStorage === 'undefined') return defaultSplitMode(role)
      const key = splitStorageKey(role)
      const raw = localStorage.getItem(key)
      if (!isSplitMode(raw)) return defaultSplitMode(role)
      // 사용자 결정 2026-09-22: 저장된 'rows'·'split' 은 1회만 기본값으로
      // 교체한다(마이그레이션 표식 없을 때만). 표식을 남긴 뒤에는 사용자가
      // 다시 그 값을 고르면 그때부터 존중한다.
      if (raw === 'rows' || raw === 'split') {
        const migratedKey = `${key}:migrated-default-v1`
        if (!localStorage.getItem(migratedKey)) {
          const fallback = defaultSplitMode(role)
          localStorage.setItem(key, fallback)
          localStorage.setItem(migratedKey, '1')
          return fallback
        }
      }
      return raw
    } catch {
      return defaultSplitMode(role)
    }
  })

  const setMode = useCallback(
    (next: SplitMode) => {
      setModeState(next)
      try {
        const key = splitStorageKey(role)
        localStorage.setItem(key, next)
        // 지금 고른 값은 1회 마이그레이션 대상이 아니다 — 표식을 같이
        // 남겨야 다음 마운트에서 'rows'·'split' 이 다시 기본값으로
        // 되돌아가지 않는다(방금 켠 분할이 바로 꺼져 보이는 사고 방지).
        localStorage.setItem(`${key}:migrated-default-v1`, '1')
      } catch {
        // 저장 실패는 무시한다 — 분할 보기는 이번 세션만 유지된다.
      }
    },
    [role],
  )

  /**
   * 통합 ↔ 나란히 보기. **3값이 되면서 왕복 규칙이 바뀐다**
   * (SPLITVIEW_ROWS_DESIGN D3-1): 끄면 `unified`, 다시 켜면 **역할 기본값**
   * 이다. `'split'` 로 되돌리면 행 정렬을 쓰던 사용자가 토글 한 번에 조용히
   * v4 2열로 내려앉는다 — 토글은 끄고 켜는 축이지 모드를 고르는 축이 아니다
   * (모드 선택은 호스트 설정 서랍이 가진다).
   */
  const toggle = useCallback(() => {
    setMode(mode === 'unified' ? NON_UNIFIED_DEFAULT_SPLIT_MODE : 'unified')
  }, [mode, setMode])

  return { mode, setMode, toggle }
}
